/* eslint-disable @typescript-eslint/no-explicit-any */
export type PromiseOrValue<T> = T | Promise<T>

export type TreeCompoundIndex<Value extends object, RetType> = {
  updateIndex: (params: {
    node: TreeNode<Value>
    oldNode: TreeNode<Value> | null
    parentItem: Value | null
    currentValue: RetType | null
    operation: 'upsert' | 'delete'
  }) => RetType
  cloneIndex: (currentValue: RetType) => RetType
}

type CompoundIndexConfig<Value extends object> = {
  [K: string]: TreeCompoundIndex<Value, any>
}

// Copy-on-write state for TreeManager
type COWTreeState<Value extends object> = {
  baseTree: TreeManager<Value, any>
  overrides: Map<string, TreeNode<Value>>
  deletedKeys: Set<string>
  isDirty: boolean
  rootOverride?: TreeNode<Value> // If root is modified, store it here
  // Store COW-specific onChange handler
  onChange?:
    | ((params: {
        node: TreeNode<Value>
        oldNode: TreeNode<Value> | null
        operation: 'upsert' | 'delete'
        tree: TreeManager<Value, any>
      }) => void)
    | null
  // Per-COW, lightweight indexes only for override nodes to keep lookups fast
  overrideIndexes: Map<keyof Value, Map<Value[keyof Value], string>>
  // Fast parent-to-children lookup map for overrides
  parentToChildrenMap: Map<string, Set<string>>
  childrenKeysCache: Map<string, string[]>
}

export type BuildTreeParams<
  Value extends object,
  TIndexes extends CompoundIndexConfig<Value> = Record<string, never>,
> = {
  /** The root key to start building the tree from */
  rootKey: string
  /** Function to get a node's key from its value */
  getKey: (value: Value) => string
  /** Function to get a node's value from its key */
  getValue: (key: string) => Value
  /** Function to get a node's children keys */
  getChildrenKeys: (key: string, value: Value) => string[]
  /** Function to get a node's order string (for sorting siblings) */
  getOrder: (key: string, value: Value) => string
  /**
   * This function is called before the item is added to the tree.
   * It can be used to modify the item before it is added to the tree, or do some other work.
   *
   * It should run just at "build time" so what happens inside is garbage collected after.
   * Don't add it to addNode, updateNode, etc.
   */
  preProcessItem?: (
    item: Value,
    options: { itemsMap: Map<string, TreeNode<Value>>; path: string[] }
  ) => Value
  compoundIndexes?: TIndexes
  indexKeys?: (keyof Value)[]
  autoFallbackWithKeys?: (keyof Value)[]
  onChange?: (params: {
    node: TreeNode<Value>
    oldNode: TreeNode<Value> | null
    operation: 'upsert' | 'delete'
    tree: TreeManager<Value, TIndexes>
  }) => void
}

// Helper type to extract the return type from a compound index
type CompoundIndexReturnType<T> =
  T extends TreeCompoundIndex<any, infer R> ? R : never

export type FlatSortedTreeItem = {
  key: string
  parentKey: string
  sortKey: string
}

export type TreeNode<Value extends object> = {
  /** A unique key for the tree node */
  key: string
  /** The value object for the tree node */
  value: Value
  /** The key of the parent node */
  parentKey: string | null
  /** Ordering string for positioning among siblings */
  order: string
}

export type ReturnTreeManager<
  Value extends object,
  TIndexes extends CompoundIndexConfig<Value> = Record<string, never>,
> = {
  root: TreeNode<Value>
  lastUpdatedAt: number
  addNode: (
    value: Value,
    partialNode: AtLeast<TreeNode<Value>, 'parentKey'>,
    options?: {
      skipOnChange?: boolean
      skipUpdateIndexes?: boolean
    }
  ) => TreeNode<Value>
  updateNode: (
    key: string,
    item: Partial<TreeNode<Value>>,
    options?: {
      skipOnChange?: boolean
      skipUpdateIndexes?: boolean
    }
  ) => TreeNode<Value>
  upsertNode: (
    value: Value,
    partialNode: Omit<Partial<TreeNode<Value>>, 'key'>,
    options?: {
      skipOnChange?: boolean
      skipUpdateIndexes?: boolean
    }
  ) => TreeNode<Value>
  getNode: <Required extends boolean>(
    key: string,
    options?: {
      required?: Required
      fallbackKey?: keyof Value | false
      allowLinearSearch?: boolean
    }
  ) => Required extends true ? TreeNode<Value> : TreeNode<Value> | null
  getPath: <
    Format extends 'array' | 'string' = 'string',
    Required extends boolean = false,
  >(
    itemOrKey: Required extends true
      ? TreeNode<Value> | string
      : TreeNode<Value> | string | undefined,
    options?: {
      returnValueKey?: keyof TreeNode<Value>['value']
      ignoreRoot?: boolean
      ignoreSelf?: boolean
      format?: Format
      required?: Required
      debug?: boolean
      fallbackKey?: keyof Value | false
      ignoreRootValidation?: boolean
    }
  ) => Required extends true
    ? Format extends 'array'
      ? string[]
      : string
    : (Format extends 'array' ? string[] : string) | null
  getFlatSortedTree__EXPENSIVE: (opts?: {
    reverse?: boolean
  }) => FlatSortedTreeItem[]
  deleteNode: (
    key: string,
    options?: { recursive?: boolean; skipOnChange?: boolean }
  ) => void
  getNodeBy: <Key extends keyof Value>(
    filterKey: Key,
    filterValue: Value[Key],
    options?: { allowLinearSearch?: boolean }
  ) => TreeNode<Value> | null
  getKey: (value: Value) => string
  /**
   * Iterate over all nodes in current tree state.
   * Works correctly with copy-on-write trees.
   */
  getAllNodes: () => IterableIterator<TreeNode<Value>>
  /**
   * @deprecated Use getAllNodes() for iteration, or specific methods for other operations.
   * This property is kept for backwards compatibility but may be slower in COW mode.
   */
  itemsMap: Map<string, TreeNode<Value>>
  indexes: Map<keyof Value, Map<Value[keyof Value], string>>
  compoundIndexes: {
    get: <K extends keyof TIndexes>(
      key: K
    ) => CompoundIndexReturnType<TIndexes[K]> | null
  }
  getChildrenKeys: (
    key: string,
    opts?: { cache?: boolean; required?: boolean }
  ) => string[]
  getValue: (key: string) => Value
  getOrder: (key: string) => string
  subscribe: (callback: () => void) => string
  unsubscribe: (id: string) => void
  /** Create a copy-on-write clone (efficient shallow clone) */
  copyOnWrite: (options?: {
    cloneChildrenKeysCache?: boolean
    onChange?:
      | ((params: {
          node: TreeNode<Value>
          oldNode: TreeNode<Value> | null
          operation: 'upsert' | 'delete'
          tree: TreeManager<Value, TIndexes>
        }) => void)
      | null
  }) => TreeManager<Value, TIndexes>
  childrenKeysCache: Map<string, string[]>
}

export const getTreeManager = <
  Value extends object,
  TIndexes extends CompoundIndexConfig<Value> = Record<string, never>,
>(
  buildTreeParams: BuildTreeParams<Value, TIndexes>,
  cowState?: COWTreeState<Value>
): ReturnTreeManager<Value, TIndexes> => {
  const itemsMap = new Map<string, TreeNode<Value>>()
  const indexes = new Map<keyof Value, Map<Value[keyof Value], string>>()
  const compoundIndexes = new Map<string, unknown>()

  // If in COW mode, initialize compound indexes from base tree with deep copy
  if (cowState && cowState.baseTree.compoundIndexes) {
    if (buildTreeParams.compoundIndexes) {
      Object.entries(buildTreeParams.compoundIndexes).forEach(
        ([key, indexConfig]) => {
          const baseValue = cowState.baseTree.compoundIndexes.get(key)
          if (baseValue !== null) {
            compoundIndexes.set(key, indexConfig.cloneIndex(baseValue as any))
          }
        }
      )
    }
  }

  // Cache for getChildrenKeys calls (per-call caching)
  const childrenKeysCache =
    cowState?.childrenKeysCache ?? new Map<string, string[]>()

  const subscribers = new Map<string, { callback: () => void }>()

  let tm: ReturnTreeManager<Value, TIndexes> | null = null

  // join with auto fallback
  const indexKeysJoined = new Set([
    ...(buildTreeParams.indexKeys ?? []),
    ...(buildTreeParams.autoFallbackWithKeys ?? []),
  ])

  function subscribe(callback: () => void): string {
    const id = crypto.randomUUID()
    subscribers.set(id, { callback })
    return id
  }

  function unsubscribe(id: string): void {
    subscribers.delete(id)
  }

  // Helper to ensure COW is triggered and mark as dirty
  function ensureCOWTriggered() {
    if (cowState && !cowState.isDirty) {
      cowState.isDirty = true
    }
  }

  const indexUpdates = new Set<string>()

  function updateIndexes({
    node,
    oldNode,
    operation,
    parentItem,
    skipOnChange,
    skipUpdateIndexes,
  }: {
    node: TreeNode<Value>
    oldNode: TreeNode<Value> | null
    operation: 'upsert' | 'delete'
    parentItem: Value | null
    skipOnChange?: boolean
    skipUpdateIndexes?: boolean
  }) {
    try {
      // Invalidate children keys cache when tree structure changes
      if (childrenKeysCache.size > 0) {
        // Always invalidate the node's parent cache (if it has one)
        if (node.parentKey) {
          childrenKeysCache.delete(node.parentKey)
        }

        // For updates where parent changes, invalidate old parent too
        if (
          operation === 'upsert' &&
          oldNode &&
          oldNode.parentKey !== node.parentKey &&
          oldNode.parentKey
        ) {
          childrenKeysCache.delete(oldNode.parentKey)
        }

        // For delete operations, the node itself might have had children, so invalidate it too
        if (operation === 'delete') {
          childrenKeysCache.delete(node.key)
        }
      }

      if (!skipUpdateIndexes) {
        if (indexUpdates.has(node.key)) {
          console.warn(
            'TreeManager: Detected reentrancy during index update. Skipping nested update to prevent infinite loop.'
          )
          // prevent infinite loops
          return
        }

        indexUpdates.add(node.key)

        // Handle COW mode
        if (cowState) {
          // Maintain lightweight indexes for overrides to accelerate lookups
          const overrideIndexes = cowState.overrideIndexes

          indexKeysJoined.forEach((indexKey) => {
            // Ensure map exists
            if (!overrideIndexes.has(indexKey)) {
              overrideIndexes.set(indexKey, new Map())
            }

            const map = overrideIndexes.get(indexKey)!

            if (operation === 'upsert') {
              // Remove old mapping if value changed
              if (
                oldNode &&
                oldNode.value[indexKey] !== node.value[indexKey] &&
                map.get(oldNode.value[indexKey]) === node.key
              ) {
                map.delete(oldNode.value[indexKey])
              }

              map.set(node.value[indexKey], node.key)
            } else if (operation === 'delete') {
              if (map.get(node.value[indexKey]) === node.key) {
                map.delete(node.value[indexKey])
              }
            }
          })

          // Update compound indexes in COW mode
          if (buildTreeParams.compoundIndexes) {
            Object.entries(buildTreeParams.compoundIndexes).forEach(
              ([indexKey, { updateIndex }]) => {
                const current = compoundIndexes.get(indexKey) ?? null
                const updated = updateIndex({
                  node,
                  oldNode,
                  parentItem,
                  currentValue: current,
                  operation,
                })
                compoundIndexes.set(indexKey, updated)
              }
            )
          }

          // Call COW-specific onChange handler if provided
          const cowOnChange = cowState.onChange
          if (cowOnChange !== null && tm && !skipOnChange) {
            if (cowOnChange) {
              // Use custom COW onChange handler
              cowOnChange({
                node,
                oldNode,
                operation,
                tree: tm!,
              })
            } else if (cowOnChange === undefined) {
              // No explicit COW onChange set, use original onChange
              buildTreeParams.onChange?.({
                node,
                oldNode,
                operation,
                tree: tm!,
              })
            }
            // If cowOnChange === null, explicitly disable onChange

            // Notify all subscribers when onChange is not explicitly disabled
            subscribers.forEach(({ callback }) => {
              try {
                callback()
              } catch (error) {
                console.error('Error in tree subscriber callback:', error)
              }
            })
          }

          if (tm) {
            // Update last modified time even in COW mode
            tm.lastUpdatedAt = Date.now()
          }
          return
        }

        indexKeysJoined.forEach((indexKey) => {
          // Create index if it doesn't exist
          if (!indexes.has(indexKey)) {
            indexes.set(indexKey, new Map())
          }

          const indexMap = indexes.get(indexKey)!

          if (operation === 'upsert') {
            indexMap.set(node.value[indexKey], node.key)
          } else if (operation === 'delete') {
            if (indexMap.get(node.value[indexKey]) === node.key) {
              indexMap.delete(node.value[indexKey])
            }
          }
        })

        // Update compound indexes
        if (buildTreeParams.compoundIndexes) {
          Object.entries(buildTreeParams.compoundIndexes).forEach(
            ([indexKey, { updateIndex }]) => {
              const current = compoundIndexes.get(indexKey) ?? null
              const updated = updateIndex({
                node,
                oldNode,
                parentItem,
                currentValue: current,
                operation,
              })
              compoundIndexes.set(indexKey, updated)
            }
          )
        }

        if (tm) {
          if (!skipOnChange) {
            // Call onChange handler if provided, only when the tm is already built
            buildTreeParams.onChange?.({
              node,
              oldNode,
              operation,
              tree: tm!,
            })
          }

          // Notify all subscribers
          subscribers.forEach(({ callback }) => {
            try {
              callback()
            } catch (error) {
              console.error('Error in tree subscriber callback:', error)
            }
          })
        }
      }
    } finally {
      indexUpdates.delete(node.key)
      // Update last modified time
      if (tm) {
        tm.lastUpdatedAt = Date.now()
      }
    }
  }

  /**
   * Internal builder. Recursively builds the tree structure.
   */
  const MAX_TREE_DEPTH = 250 // Low enough to prevent stack overflow, high enough for legitimate trees

  function buildTree({
    key,
    parentKey,
    parentPath,
    parentItem,
    visitedKeys = new Set<string>(),
    depth = 0,
  }: {
    key: string
    parentKey: string | null
    parentPath: string[]
    parentItem: Value | null
    visitedKeys?: Set<string>
    depth?: number
  }): TreeNode<Value> | null {
    // Check for excessive depth - return null to skip this branch (prevents stack overflow)
    if (depth > MAX_TREE_DEPTH) {
      console.error(
        `[TreeManager] Max tree depth (${MAX_TREE_DEPTH}) exceeded. Skipping branch.`,
        { key, parentKey }
      )
      return null
    }

    // Check for infinite loop - if we've already visited this key in the current path
    if (visitedKeys.has(key)) {
      console.error(`[TreeManager] Circular reference detected: Node "${key}" already in path. Skipping.`)
      return null
    }

    // Add current key to visited set for this traversal path
    const newVisitedKeys = new Set(visitedKeys)
    newVisitedKeys.add(key)

    const path = [...parentPath, key]
    let value = buildTreeParams.getValue(key)

    value = buildTreeParams.preProcessItem
      ? buildTreeParams.preProcessItem(value, { itemsMap, path })
      : value

    const node: TreeNode<Value> = {
      key,
      parentKey,
      value,
      order: buildTreeParams.getOrder(key, value),
    }
    itemsMap.set(key, node)
    updateIndexes({ node, oldNode: null, operation: 'upsert', parentItem })

    const childrenKeys = buildTreeParams.getChildrenKeys(key, value)

    if (childrenKeys) {
      const validChildKeys: string[] = []
      childrenKeys.forEach((childKey) => {
        try {
          const childNode = buildTree({
            key: childKey,
            parentKey: key,
            parentPath: path,
            parentItem: value,
            visitedKeys: newVisitedKeys,
            depth: depth + 1,
          })
          if (childNode) {
            validChildKeys.push(childKey)
          }
        } catch (e) {
          // Skip this child branch if it causes an error (e.g., stack overflow)
          console.error(`[TreeManager] Error building child "${childKey}". Skipping branch.`)
        }
      })
      if (validChildKeys.length > 0) {
        childrenKeysCache.set(key, validChildKeys)
      }
    }

    return node
  }

  // Build the root tree (unless in COW mode where we use existing tree)
  const builtRoot = cowState
    ? cowState.rootOverride || cowState.baseTree.root
    : buildTree({
        key: buildTreeParams.rootKey,
        parentKey: null,
        parentPath: [],
        parentItem: null,
        visitedKeys: new Set<string>(),
      })

  if (!builtRoot) {
    throw new TreeManagerError(
      `Failed to build tree: root node "${buildTreeParams.rootKey}" could not be created.`
    )
  }

  let root = builtRoot

  function addNode(
    value: Value,
    partialNode: AtLeast<TreeNode<Value>, 'parentKey'>,
    options?: {
      skipOnChange?: boolean
      skipUpdateIndexes?: boolean
    }
  ): TreeNode<Value> {
    const parentKey = partialNode.parentKey
    const key = buildTreeParams.getKey(value)

    // Check if node already exists
    if (getNode(key, { fallbackKey: false })) {
      throw new TreeManagerError(`Node with key ${key} already exists`)
    }

    const node: TreeNode<Value> = {
      key,
      parentKey,
      value,
      order: partialNode.order ?? buildTreeParams.getOrder(key, value),
    }

    if (cowState) {
      ensureCOWTriggered()
      cowState.overrides.set(key, node)
      cowState.deletedKeys.delete(key) // Remove from deleted if it was there

      // Update parent-to-children mapping
      if (node.parentKey) {
        if (!cowState.parentToChildrenMap.has(node.parentKey)) {
          cowState.parentToChildrenMap.set(node.parentKey, new Set())
        }
        cowState.parentToChildrenMap.get(node.parentKey)!.add(key)
      }

      // Handle root override
      if (key === root.key) {
        cowState.rootOverride = node
      }
    } else {
      itemsMap.set(key, node)
    }

    const parentItem = parentKey
      ? (getNode(parentKey, { fallbackKey: false })?.value ?? null)
      : null
    updateIndexes({
      node,
      oldNode: null,
      operation: 'upsert',
      parentItem,
      skipOnChange: options?.skipOnChange,
      skipUpdateIndexes: options?.skipUpdateIndexes,
    })

    return node
  }

  function updateNode(
    key: string,
    item: Partial<TreeNode<Value>>,
    options?: {
      skipOnChange?: boolean
      skipUpdateIndexes?: boolean
    }
  ): TreeNode<Value> {
    const existingNode = getNode(key, { required: true })
    const oldNode = { ...existingNode }

    // Create updated node
    const updatedNode: TreeNode<Value> = {
      ...existingNode,
      ...item,
      key: existingNode.key,
      parentKey:
        item.parentKey !== undefined ? item.parentKey : existingNode.parentKey,
      value: item.value ?? existingNode.value,
      order: item.order ?? existingNode.order,
    }

    if (cowState) {
      ensureCOWTriggered()
      cowState.overrides.set(existingNode.key, updatedNode)

      // Update parent-to-children mapping if parent changed
      if (oldNode.parentKey !== updatedNode.parentKey) {
        // Remove from old parent's children
        if (oldNode.parentKey) {
          const oldParentChildren = cowState.parentToChildrenMap.get(
            oldNode.parentKey
          )
          if (oldParentChildren) {
            oldParentChildren.delete(existingNode.key)
            if (oldParentChildren.size === 0) {
              cowState.parentToChildrenMap.delete(oldNode.parentKey)
            }
          }
        }

        // Add to new parent's children
        if (updatedNode.parentKey) {
          if (!cowState.parentToChildrenMap.has(updatedNode.parentKey)) {
            cowState.parentToChildrenMap.set(updatedNode.parentKey, new Set())
          }
          cowState.parentToChildrenMap
            .get(updatedNode.parentKey)!
            .add(existingNode.key)
        }
      }

      // Handle root override - update the root variable too
      if (existingNode.key === root.key) {
        cowState.rootOverride = updatedNode
        root = updatedNode // Update the local root variable
      }
    } else {
      if (existingNode.key === root.key) {
        root = updatedNode
      }
      itemsMap.set(existingNode.key, updatedNode)
    }

    const parentItem = updatedNode.parentKey
      ? (getNode(updatedNode.parentKey, { fallbackKey: false })?.value ?? null)
      : null

    updateIndexes({
      node: updatedNode,
      oldNode,
      operation: 'upsert',
      parentItem,
      skipOnChange: options?.skipOnChange,
      skipUpdateIndexes: options?.skipUpdateIndexes,
    })

    return updatedNode
  }

  function upsertNode(
    value: Value,
    partialNode: Omit<Partial<TreeNode<Value>>, 'key'>,
    options?: {
      skipOnChange?: boolean
      skipUpdateIndexes?: boolean
    }
  ): TreeNode<Value> {
    const key = buildTreeParams.getKey(value)
    const existingNode = getNode(key, { fallbackKey: false })

    if (existingNode) {
      return updateNode(
        key,
        {
          ...partialNode,
          value,
        },
        {
          skipOnChange: options?.skipOnChange,
          skipUpdateIndexes: options?.skipUpdateIndexes,
        }
      )
    } else {
      return addNode(
        value,
        {
          parentKey: partialNode.parentKey ?? null,
          ...partialNode,
        },
        {
          skipOnChange: options?.skipOnChange,
        }
      )
    }
  }

  function deleteNode(
    key: string,
    opts?: { recursive?: boolean; skipOnChange?: boolean }
  ): void {
    const { recursive = false } = opts ?? {}
    const node = getNode(key, { fallbackKey: false })
    if (!node) return

    if (recursive) {
      // Delete all children first
      const childKeys = getChildrenKeys(key)
      childKeys.forEach((childKey) => {
        deleteNode(childKey, {
          recursive: true,
          skipOnChange: opts?.skipOnChange,
        })
      })
    }

    if (cowState) {
      ensureCOWTriggered()
      cowState.deletedKeys.add(key)
      cowState.overrides.delete(key) // Remove from overrides if it was there

      // Remove from parent-to-children mapping
      if (node.parentKey) {
        const parentChildren = cowState.parentToChildrenMap.get(node.parentKey)
        if (parentChildren) {
          parentChildren.delete(key)
          if (parentChildren.size === 0) {
            cowState.parentToChildrenMap.delete(node.parentKey)
          }
        }
      }

      // Also remove any children this node had from the mapping (they become orphaned)
      cowState.parentToChildrenMap.delete(key)

      // Handle root deletion (though this is unusual)
      if (key === root.key) {
        cowState.rootOverride = undefined
      }
    } else {
      itemsMap.delete(key)
    }

    updateIndexes({
      node,
      oldNode: node,
      operation: 'delete',
      parentItem: null,
      skipOnChange: opts?.skipOnChange,
    })
  }

  const fallbackKeys = buildTreeParams.autoFallbackWithKeys ?? []

  function getNode<Required extends boolean>(
    key: string,
    options?: {
      required?: Required
      fallbackKey?: keyof Value | false
      allowLinearSearch?: boolean
    }
  ): Required extends true ? TreeNode<Value> : TreeNode<Value> | null {
    if (typeof key === 'undefined') {
      if (options?.required) {
        throw new TreeManagerError(
          'Node is required, but received undefined as the lookup key.'
        )
      }
      return null as any
    }

    // COW-aware node retrieval
    if (cowState) {
      // 1) Direct override by exact key (do not consult deletedKeys using the input key)
      if (cowState.overrides.has(key)) {
        const node = cowState.overrides.get(key)!
        if (!cowState.deletedKeys.has(node.key)) return node as any
        // If the resolved node key is deleted, treat as not found and continue
      }

      // 2B) Direct lookup in base tree first (original order)
      // Note: base tree may resolve via its own fallback logic. We validate against COW deletions below.
      const baseNode = cowState.baseTree.getNode(key, {
        ...options,
        required: false,
      })

      if (baseNode && !cowState.deletedKeys.has(baseNode.key)) {
        if (cowState.overrides.has(baseNode.key)) {
          const override = cowState.overrides.get(baseNode.key) as any
          // ensure override matches the fallbackKey
          let matches = false
          if (options?.fallbackKey) {
            matches =
              override.value[options.fallbackKey] ===
              baseNode.value[options.fallbackKey]
          } else if (
            options?.fallbackKey !== false &&
            fallbackKeys.length > 0
          ) {
            for (const fallbackKey of fallbackKeys) {
              if (fallbackKey === undefined) continue
              matches =
                override.value[fallbackKey] === baseNode.value[fallbackKey]
              if (matches) break
            }
          }
          if (matches) return override
        } else {
          return baseNode as any
        }
      }

      // 3B) Fallback lookups by indexed/fallback keys
      if (options?.fallbackKey) {
        const byFallback = getNodeBy(options.fallbackKey, key as any, {
          allowLinearSearch: options?.allowLinearSearch ?? false,
        })
        if (byFallback) return byFallback as any
      } else if (options?.fallbackKey !== false && fallbackKeys.length > 0) {
        for (const fallbackKey of fallbackKeys) {
          if (fallbackKey === undefined) continue
          const byFallback = getNodeBy(fallbackKey, key as any, {
            allowLinearSearch: options?.allowLinearSearch ?? false,
          })
          if (byFallback) return byFallback as any
        }
      }

      if (options?.required) {
        throw new TreeManagerError(`Node ${key} not found`)
      }
      return null as any
    }

    let node = itemsMap.get(key) ?? null
    if (node) return node

    if (options?.fallbackKey) {
      node = getNodeBy(options.fallbackKey, key as any, {
        allowLinearSearch: options?.allowLinearSearch ?? false,
      })
      if (node) return node
    } else if (options?.fallbackKey !== false && fallbackKeys.length > 0) {
      for (const fallbackKey of fallbackKeys) {
        if (fallbackKey === undefined) continue
        node = getNodeBy(fallbackKey, key as any, {
          allowLinearSearch: options?.allowLinearSearch ?? false,
        })
        if (node) return node
      }
    }

    if (node) return node

    if (options?.required) {
      throw new TreeManagerError(`Node ${key} not found`)
    }
    return null as any
  }

  function getNodeBy<Key extends keyof Value>(
    filterKey: Key,
    filterValue: Value[Key],
    options?: { allowLinearSearch?: boolean }
  ): TreeNode<Value> | null {
    if (typeof filterKey === 'undefined') return null

    // COW-aware node lookup
    if (cowState) {
      // First, consult overrideIndexes for fast lookup
      const overrideIdxMap = cowState.overrideIndexes.get(filterKey as any) as
        | Map<Value[Key], string>
        | undefined

      if (overrideIdxMap) {
        const candidateKey = overrideIdxMap.get(filterValue)
        if (
          candidateKey &&
          !cowState.deletedKeys.has(candidateKey) &&
          cowState.overrides.has(candidateKey)
        ) {
          return cowState.overrides.get(candidateKey) as TreeNode<Value>
        }
      }

      // Check if we should do linear search through overrides (only if explicitly allowed)
      if (options?.allowLinearSearch && !overrideIdxMap) {
        // Fallback: iterate overrides (should be small) if index absent and allowed
        for (const [
          overrideKey,
          overrideNode,
        ] of cowState.overrides.entries()) {
          if (
            !cowState.deletedKeys.has(overrideKey) &&
            overrideNode.value[filterKey] === filterValue
          ) {
            return overrideNode
          }
        }
      }

      // Then check base tree, excluding deleted keys and overridden keys
      const baseResult = cowState.baseTree.getNodeBy(
        filterKey,
        filterValue,
        options
      )
      if (baseResult) {
        const baseKey = baseResult.key
        if (
          !cowState.deletedKeys.has(baseKey) &&
          !cowState.overrides.has(baseKey)
        ) {
          return baseResult
        }
      }

      return null
    }

    // For non-COW, check indexes first for better performance
    if (indexes.has(filterKey)) {
      const indexMap = indexes.get(filterKey)!
      const nodeKey = indexMap.get(filterValue)
      return nodeKey ? (itemsMap.get(nodeKey) ?? null) : null
    }

    // No index available - throw error unless linear search is explicitly allowed
    if (!options?.allowLinearSearch) {
      throw new TreeManagerError(
        `No index found for key '${String(filterKey)}'. ` +
          `Available indexes: [${Array.from(indexes.keys())
            .map((k) => String(k))
            .join(', ')}]. ` +
          `Either add '${String(filterKey)}' to indexKeys, or pass { allowLinearSearch: true } if you really want O(n) performance.`
      )
    }

    // Explicit linear search fallback (only when allowed)
    for (const node of itemsMap.values()) {
      if (node.value[filterKey] === filterValue) {
        return node
      }
    }

    return null
  }

  function getChildrenKeys(
    key: string,
    opts?: { cache?: boolean; required?: boolean }
  ): string[] {
    const shouldCache = opts?.cache ?? true

    let result: string[]
    const node = getNode(key, { required: opts?.required })
    if (!node) return []
    key = node.key

    // Check cache first if caching is enabled (default)
    if (shouldCache && childrenKeysCache.has(key)) {
      return childrenKeysCache.get(key)!
    }

    // COW-aware children retrieval
    if (cowState) {
      // Start with base children, excluding deleted ones and those that have been moved
      const baseChildren = cowState.baseTree
        .getChildrenKeys(key)
        .filter((childKey) => {
          // Exclude if deleted
          if (cowState.deletedKeys.has(childKey)) return false

          // Exclude if this child has been moved to a different parent in overrides
          const overrideChild = cowState.overrides.get(childKey)
          if (overrideChild && overrideChild.parentKey !== key) return false

          return true
        })

      // Add any new children from overrides that have this node as parent
      const overrideChildren: string[] = []
      const overrideChildrenSet = cowState.parentToChildrenMap.get(key)
      if (overrideChildrenSet) {
        for (const overrideKey of overrideChildrenSet) {
          if (
            !cowState.deletedKeys.has(overrideKey) &&
            !baseChildren.includes(overrideKey)
          ) {
            overrideChildren.push(overrideKey)
          }
        }
      }

      result = [...baseChildren, ...overrideChildren]
    } else {
      result = buildTreeParams.getChildrenKeys(key, node.value)
    }

    // Cache the result if caching is requested
    if (shouldCache) {
      childrenKeysCache.set(key, result)
    }

    return result
  }

  function getPath<
    Format extends 'array' | 'string' = 'string',
    Required extends boolean = false,
  >(
    itemOrKey: Required extends true
      ? TreeNode<Value> | string
      : TreeNode<Value> | string | undefined,
    options?: {
      returnValueKey?: keyof TreeNode<Value>['value']
      ignoreRoot?: boolean
      ignoreSelf?: boolean
      format?: Format
      required?: Required
      debug?: boolean
      fallbackKey?: keyof Value | false
      ignoreRootValidation?: boolean
    }
  ): Required extends true
    ? Format extends 'array'
      ? string[]
      : string
    : (Format extends 'array' ? string[] : string) | null {
    const { ignoreRoot, ignoreSelf, format = 'string', debug } = options || {}

    const node = (
      typeof itemOrKey === 'string'
        ? getNode(itemOrKey, { fallbackKey: options?.fallbackKey })
        : itemOrKey
    ) as TreeNode<Value> | null

    function applyFormat(
      arr: string[]
    ): Format extends 'array' ? string[] : string {
      if (format === 'array') return arr as any
      return arr.join('/') as any
    }

    if (!node) {
      if (options?.required) {
        throw new TreeManagerError(`Node for key ${itemOrKey} not found`)
      }
      return null as any
    }

    const path: string[] = []

    const isRecursiveCall = node.key === node.parentKey

    const parent = node.parentKey ? getNode(node.parentKey) : null
    if (parent && !isRecursiveCall) {
      const parentPath = getPath(parent.key, {
        format: 'array',
        ignoreRoot: false,
        ignoreSelf: false,
        returnValueKey: options?.returnValueKey,
        debug,
        fallbackKey: options?.fallbackKey,
      })
      path.push(...(parentPath ?? []))
    }

    if (!ignoreSelf) {
      const value = options?.returnValueKey
        ? node.value[options.returnValueKey]
        : node.key
      if (typeof value !== 'string') {
        if (
          !!options?.returnValueKey &&
          typeof value === 'undefined' &&
          node?.key === root.key
        ) {
          path.push('root')
        } else {
          throw new TreeManagerError(
            `Path value must be a string, got ${typeof value} for itemOrKey ${JSON.stringify(itemOrKey)}`
          )
        }
      } else {
        path.push(value)
      }
    }

    if (
      !options?.returnValueKey &&
      !options?.ignoreRootValidation &&
      path[0] !== root.key
    ) {
      if (!options?.required) return null as any

      throw new TreeManagerError(
        `Path must always start with root key ("${root.key}"). Got "${
          path[0]
        }". Error while getting path for ${
          typeof itemOrKey === 'string' ? itemOrKey : itemOrKey?.key
        }`
      )
    }

    if (ignoreRoot) path.shift()

    return applyFormat(path)
  }

  /**
   * Returns a flat array of all nodes in the tree, sorted by their order strings.
   * Warning: This operation is expensive for large trees as it traverses the entire structure.
   */
  function getFlatSortedTree__EXPENSIVE(opts?: {
    reverse?: boolean
  }): FlatSortedTreeItem[] {
    const flatTree: Array<FlatSortedTreeItem> = []

    function processNode(
      key: string,
      parentKey: string,
      parentSortKey: string,
      index: number
    ) {
      const sortKey = `${parentSortKey}.${index}`
      flatTree.push({ key, parentKey, sortKey })

      if (!tm) {
        throw new Error('tm is not defined')
      }

      const childrenKeys = tm.getChildrenKeys(key)
      if (childrenKeys) {
        childrenKeys.forEach((childKey, childIndex) => {
          processNode(childKey, key, sortKey, childIndex)
        })
      }
    }

    processNode(root.key, '', '', 0)

    return flatTree.sort((a, b) => compareSortKeys(a, b, opts))
  }

  tm = {
    get root() {
      // Always return the current root, which may be overridden in COW mode
      if (cowState && cowState.rootOverride) {
        return cowState.rootOverride
      }
      return root
    },
    lastUpdatedAt: Date.now(),
    addNode,
    updateNode,
    upsertNode,
    getNode,
    getPath,
    getFlatSortedTree__EXPENSIVE,
    deleteNode,
    getNodeBy,
    getKey: buildTreeParams.getKey,
    getAllNodes,
    childrenKeysCache,
    get itemsMap(): Map<string, TreeNode<Value>> {
      if (cowState) {
        // Build a COW-aware map that includes all current nodes
        const result = new Map<string, TreeNode<Value>>()
        for (const node of getAllNodes()) {
          result.set(node.key, node)
        }
        return result
      }
      return itemsMap // Return internal map for regular trees
    },
    indexes,
    compoundIndexes: {
      get: <K extends keyof TIndexes>(key: K) =>
        compoundIndexes.get(key as string) as CompoundIndexReturnType<
          TIndexes[K]
        > | null,
    },
    getChildrenKeys,
    getValue: buildTreeParams.getValue,
    getOrder: (key) =>
      buildTreeParams.getOrder(
        key,
        getNode(key, { required: true, fallbackKey: false }).value
      ),
    subscribe,
    unsubscribe,
    copyOnWrite: (options?: {
      cloneChildrenKeysCache?: boolean
      onChange?:
        | ((params: {
            node: TreeNode<Value>
            oldNode: TreeNode<Value> | null
            operation: 'upsert' | 'delete'
            tree: TreeManager<Value, TIndexes>
          }) => void)
        | null
    }) => {
      // For nested COW: if we're already a COW, create a virtual base that includes our current state
      if (cowState) {
        // Create a new TreeManager that represents the current COW state as the base
        const virtualBaseParams = {
          ...buildTreeParams,
          getValue: (key: string) => {
            const node = getNode(key, { required: true })
            return node.value
          },
          getChildrenKeys: (key: string) => {
            return getChildrenKeys(key)
          },
          getOrder: (key: string) => {
            const node = getNode(key, { required: true })
            return node.order
          },
        }

        // Create virtual base tree with current state
        const virtualBase = getTreeManager(virtualBaseParams)!

        // Copy all current state to the virtual base
        for (const node of getAllNodes()) {
          virtualBase.itemsMap.set(node.key, { ...node })
        }

        const newCOWState: COWTreeState<Value> = {
          baseTree: virtualBase,
          overrides: new Map(),
          deletedKeys: new Set(),
          isDirty: false,
          rootOverride: undefined,
          onChange: options?.onChange,
          overrideIndexes: new Map(),
          parentToChildrenMap: new Map(),
          childrenKeysCache: options?.cloneChildrenKeysCache
            ? new Map(childrenKeysCache)
            : new Map(),
        }

        const result = getTreeManager(buildTreeParams, newCOWState)!
        return result
      } else {
        // Regular COW from a non-COW tree
        const newCOWState: COWTreeState<Value> = {
          baseTree: tm!,
          overrides: new Map(),
          deletedKeys: new Set(),
          isDirty: false,
          rootOverride: undefined,
          onChange: options?.onChange,
          overrideIndexes: new Map(),
          parentToChildrenMap: new Map(),
          childrenKeysCache: options?.cloneChildrenKeysCache
            ? new Map(childrenKeysCache)
            : new Map(),
        }

        const result = getTreeManager(buildTreeParams, newCOWState)!
        return result
      }
    },
  }

  // Helper function to get all nodes in current state
  function* getAllNodes(): IterableIterator<TreeNode<Value>> {
    if (cowState) {
      // Yield from overrides first
      for (const [key, node] of cowState.overrides.entries()) {
        if (!cowState.deletedKeys.has(key)) {
          yield node
        }
      }

      // Then yield from base tree, excluding overridden and deleted keys
      for (const node of cowState.baseTree.getAllNodes()) {
        if (
          !cowState.deletedKeys.has(node.key) &&
          !cowState.overrides.has(node.key)
        ) {
          yield node
        }
      }
    } else {
      yield* itemsMap.values()
    }
  }

  return tm!
}

export type TreeManager<
  Value extends object,
  TIndexes extends CompoundIndexConfig<Value> = Record<string, never>,
> = ReturnTreeManager<Value, TIndexes>

export type AtLeast<T, K extends keyof T> = Partial<T> & Pick<T, K>

export type TreeManagerMethods<
  Value extends object,
  TIndexes extends CompoundIndexConfig<Value> = Record<string, never>,
> = AtLeast<
  ReturnTreeManager<Value, TIndexes>,
  | 'getNode'
  | 'getChildrenKeys'
  | 'getValue'
  | 'getOrder'
  | 'compoundIndexes'
  | 'root'
  | 'lastUpdatedAt'
  | 'updateNode'
  | 'addNode'
  | 'deleteNode'
  | 'getPath'
  | 'getFlatSortedTree__EXPENSIVE'
  | 'getNodeBy'
  | 'getKey'
  | 'getAllNodes'
>

// util
export class TreeManagerError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'TreeManagerError'
  }
}

type CompareSortKeysOpts = { separator?: string; reverse?: boolean }
export function compareSortKeys(
  a: { sortKey: string },
  b: { sortKey: string },
  opts?: CompareSortKeysOpts
) {
  const { separator = '.', reverse = false } = opts ?? {}
  const aParts = a.sortKey.split(separator).map(Number)
  const bParts = b.sortKey.split(separator).map(Number)

  const maxLength = Math.max(aParts.length, bParts.length)

  for (let i = 0; i < maxLength; i++) {
    if (aParts[i] === undefined) {
      return reverse ? 1 : -1
    } else if (bParts[i] === undefined) {
      return 1
    } else if (aParts[i] !== bParts[i]) {
      return (
        ((aParts[i] as number) - (bParts[i] as number)) * (reverse ? -1 : 1)
      )
    }
  }

  return 0
}

export * from './old-tm'
